import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Body1, Display5, durations, NavHeader, YStack } from "@medbillai/ui";

// OnboardingScreen is reused here as a generic onboarding-style layout for this
// in-flow experiment, even though the link flow isn't onboarding proper.
// this is temporary until we move the link flow into the new modal component
import { OnboardingScreen } from "../../../components/OnboardingScreen";
import type { StepAction, StepConfig } from "./types";
const FADE_DURATION_MS = durations.short;
// Tamagui's `transition` prop requires a literal string, so we can't derive
// this from FADE_DURATION_MS. Keep the two in sync if durations.short changes.
const FADE_TRANSITION = "300ms ease" as const;
const THANK_YOU_AUTO_EXIT_MS = 2500;
const StepBody = ({
  step
}: {
  step: StepConfig;
}) => <YStack flex={1} $gtSm={{
  flex: 0
}} data-sentry-element="YStack" data-sentry-component="StepBody" data-sentry-source-file="FakeDoorMmfaStep.tsx">
    {step.content}
  </YStack>;
const StepTitle = ({
  step
}: {
  step: StepConfig;
}) => {
  if (!step.title && !step.subtitle) {
    return null;
  }
  return <YStack paddingHorizontal="$sp3" alignItems="center" gap="$sp1" data-sentry-element="YStack" data-sentry-component="StepTitle" data-sentry-source-file="FakeDoorMmfaStep.tsx">
      {step.title ? <Display5 textAlign="center">{step.title}</Display5> : null}
      {step.subtitle ? <Body1 color="$textSubtle" textAlign="center">
          {step.subtitle}
        </Body1> : null}
    </YStack>;
};
const StepChrome = ({
  progress,
  title,
  showBackButton,
  onBackPress,
  onClosePress,
  footer,
  children
}: {
  progress: number;
  title: string;
  showBackButton: boolean;
  onBackPress: () => void;
  onClosePress: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) =>
// Renders inside the accounts-link pages' own chrome, so the screen keeps
// its content-hugging footer instead of the viewport pin.
<OnboardingScreen pinnedFooter={false} data-sentry-element="OnboardingScreen" data-sentry-component="StepChrome" data-sentry-source-file="FakeDoorMmfaStep.tsx">
    <NavHeader variant="progressBar" progress={progress} title={title} showBackButton={showBackButton} onBackPress={onBackPress} onClosePress={onClosePress} data-sentry-element="NavHeader" data-sentry-source-file="FakeDoorMmfaStep.tsx" />
    <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="FakeDoorMmfaStep.tsx">{children}</OnboardingScreen.Content>
    {footer}
  </OnboardingScreen>;
export const FakeDoorMmfaStep = ({
  step,
  progress,
  canGoBack,
  onAction,
  onBack,
  onDismiss
}: {
  step: StepConfig;
  progress: number;
  canGoBack: boolean;
  onAction: (action: StepAction) => void;
  onBack: () => void;
  onDismiss: () => void;
}) => {
  const [displayed, setDisplayed] = useState({
    step,
    progress,
    canGoBack
  });
  const [isFading, setIsFading] = useState(false);
  useEffect(() => {
    if (step.id === displayed.step.id) {
      setDisplayed({
        step,
        progress,
        canGoBack
      });
      setIsFading(false);
      return;
    }
    setIsFading(true);
    const timer = setTimeout(() => {
      setDisplayed({
        step,
        progress,
        canGoBack
      });
      setIsFading(false);
    }, FADE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [step, progress, canGoBack, displayed.step.id]);
  const displayStep = displayed.step;
  useEffect(() => {
    if (displayStep.id !== "thank_you") {
      return;
    }
    const timer = setTimeout(onDismiss, THANK_YOU_AUTO_EXIT_MS);
    return () => clearTimeout(timer);
  }, [displayStep.id, onDismiss]);
  const isThankYou = displayStep.id === "thank_you";
  const headerTitle = displayStep.title ?? "";
  const {
    primary,
    secondary
  } = displayStep;
  const footer = !isThankYou && primary ? <OnboardingScreen.Footer isFading={isFading}>
        <OnboardingScreen.PrimaryAction disabled={isFading} onPress={() => onAction(primary.action)}>
          {primary.label}
        </OnboardingScreen.PrimaryAction>
        {secondary ? <OnboardingScreen.TertiaryAction disabled={isFading} onPress={() => onAction(secondary.action)}>
            {secondary.label}
          </OnboardingScreen.TertiaryAction> : null}
      </OnboardingScreen.Footer> : undefined;
  return <StepChrome progress={displayed.progress} title={headerTitle} showBackButton={displayed.canGoBack} onBackPress={onBack} onClosePress={onDismiss} footer={footer} data-sentry-element="StepChrome" data-sentry-component="FakeDoorMmfaStep" data-sentry-source-file="FakeDoorMmfaStep.tsx">
      {isThankYou ? <YStack opacity={isFading ? 0 : 1} transition={FADE_TRANSITION}>
          {displayStep.content}
        </YStack> : <YStack flex={1} opacity={isFading ? 0 : 1} transition={FADE_TRANSITION} gap="$sp7">
          <StepTitle step={displayStep} />
          <StepBody step={displayStep} />
        </YStack>}
    </StepChrome>;
};